import React from 'react';
import { CheckCircle, Eye, Clock } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * BookAnalyticsDetailModal — full per-book reader breakdown shown when an
 * admin clicks a row in the Analytics table. Self-contained, no internal
 * state — fully controlled via `analytics` payload and `onClose`.
 *
 * Extracted from AdminSettings.jsx (2026-05-29) to keep that page below
 * the 1300-line threshold and make the modal easy to reuse / test in
 * isolation.
 */
const BookAnalyticsDetailModal = ({ analytics, onClose }) => {
  if (!analytics) return null;

  const { book, totals, readers } = analytics;

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      data-testid="book-analytics-detail-modal"
    >
      <div className="bg-gray-900 rounded-xl p-6 w-full max-w-2xl border border-white/10 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-white">{book.title}</h3>
            <p className="text-gray-400">by {book.author}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
            data-testid="book-analytics-close"
          >
            ✕
          </Button>
        </div>

        {/* Totals Summary */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white/5 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-white">{totals.total}</p>
            <p className="text-xs text-gray-400">Total</p>
          </div>
          <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-yellow-400">{totals.started}</p>
            <p className="text-xs text-gray-400">Started</p>
          </div>
          <div className="bg-blue-500/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-blue-400">{totals.partial}</p>
            <p className="text-xs text-gray-400">Partial</p>
          </div>
          <div className="bg-green-500/10 rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-400">{totals.completed}</p>
            <p className="text-xs text-gray-400">Completed</p>
          </div>
        </div>

        {/* Readers List */}
        {totals.total === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No readers yet for this book.
          </div>
        ) : (
          <div className="space-y-4">
            {/* Completed Readers */}
            {readers.completed.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-green-400 mb-2 flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Completed ({readers.completed.length})
                </h4>
                <div className="space-y-2">
                  {readers.completed.map((reader, idx) => (
                    <div key={reader.user_id || reader.user_email || `completed-${idx}`} className="bg-green-500/10 rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="text-white text-sm">{reader.user_name}</p>
                        <p className="text-gray-500 text-xs">{reader.user_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-green-400 text-sm font-medium">100%</p>
                        {reader.completed_at && (
                          <p className="text-gray-500 text-xs">
                            {new Date(reader.completed_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Partial Readers */}
            {readers.partial.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-blue-400 mb-2 flex items-center">
                  <Eye className="h-4 w-4 mr-1" />
                  Partial Reads ({readers.partial.length})
                </h4>
                <div className="space-y-2">
                  {readers.partial.map((reader, idx) => (
                    <div key={reader.user_id || reader.user_email || `partial-${idx}`} className="bg-blue-500/10 rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="text-white text-sm">{reader.user_name}</p>
                        <p className="text-gray-500 text-xs">{reader.user_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-blue-400 text-sm font-medium">{reader.progress}%</p>
                        <p className="text-gray-500 text-xs">
                          {new Date(reader.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Started Readers */}
            {readers.started.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-yellow-400 mb-2 flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  Just Started ({readers.started.length})
                </h4>
                <div className="space-y-2">
                  {readers.started.map((reader, idx) => (
                    <div key={reader.user_id || reader.user_email || `started-${idx}`} className="bg-yellow-500/10 rounded-lg p-3 flex justify-between items-center">
                      <div>
                        <p className="text-white text-sm">{reader.user_name}</p>
                        <p className="text-gray-500 text-xs">{reader.user_email}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-yellow-400 text-sm font-medium">{reader.progress}%</p>
                        <p className="text-gray-500 text-xs">
                          {new Date(reader.started_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <Button
          onClick={onClose}
          className="w-full mt-6 bg-red-600 hover:bg-red-700"
          data-testid="book-analytics-close-footer"
        >
          Close
        </Button>
      </div>
    </div>
  );
};

export default BookAnalyticsDetailModal;
